import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, InputAdornment, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import LoadingButton from '@mui/lab/LoadingButton';
import { openCashRegister, getLastCloseCashRegister } from '../../../../services/CashRegisterService';
import { useCurrencies } from '../../../../context/CurrencyContext';
import ForeignCurrencyCashForm, { buildCurrencyBalances } from './ForeignCurrencyCashForm';
import CashCurrencyStepper, { currencyHeading } from './CashCurrencyStepper';
import { DEFAULT_CURRENCY } from '../../../../utils/currency';

const OpenCashRegisterModal = ({ open, onClose, onSuccess, locationId, terminalId }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [discrepancy, setDiscrepancy] = useState(false);
    const [lastClose, setLastClose] = useState(null);
    const [attempts, setAttempts] = useState(0);
    // Paso 2 (solo si el tenant tiene otras monedas habilitadas): efectivo en cada moneda distinta de pesos.
    const { currencies, multiCurrency } = useCurrencies();
    const foreignCurrencies = currencies.filter((c) => c.code !== DEFAULT_CURRENCY);
    const [step, setStep] = useState(0);
    const [foreignValues, setForeignValues] = useState({});

    const [formData, setFormData] = useState({
        dec2000: '',
        dec1000: '',
        dec500: '',
        dec200: '',
        dec100: '',
        dec50: '',
        dec20: '',
        dec10: '',
        dec5: '',
        dec2: '',
        dec1: '',
    });

    const [fieldErrors, setFieldErrors] = useState({});

    useEffect(() => {
        if (open) {
            setLoading(true);
            setAttempts(0);
            setStep(0);
            setForeignValues({});
            getLastCloseCashRegister(locationId, terminalId)
                .then((data) => {
                    if (data) {
                        setLastClose(data);
                    }
                })
                .catch((err) => console.error("Error obteniendo último cierre:", err))
                .finally(() => setLoading(false));
        }
    }, [open, locationId, terminalId]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const validateDiscrepancy = () => {
        if (!lastClose || attempts > 0) return false;

        let hasDiscrepancy = false;
        const newErrors = {};

        Object.keys(formData).forEach((key) => {
            if (parseInt(formData[key]) !== parseInt(lastClose[key] || 0)) {
                newErrors[key] = true;
                hasDiscrepancy = true;
            }
        });

        setFieldErrors(newErrors);
        return hasDiscrepancy;
    };

    const handleSubmit = async () => {
        const allFieldsFilled = Object.values(formData).every(value => value.trim() !== '');
        if (!allFieldsFilled) {
            setError(true);
            return;
        }

        if (!lastClose) {
            setDiscrepancy(false);
        } else if (attempts === 0) {
            const hasDiscrepancy = validateDiscrepancy();
            setDiscrepancy(hasDiscrepancy);
            if (hasDiscrepancy) {
                setAttempts(1);
                return;
            }
        }

        if (multiCurrency && step < foreignCurrencies.length) {
            setStep(step + 1);
            return;
        }

        setLoading(true);
        setError(false);

        try {
            const result = await openCashRegister({
                location_id: parseInt(locationId),
                terminal_code: terminalId,
                efectivo: 0.0,
                pos: 0.0,
                pedidos_ya: 0.0,
                mercado_pago: 0.0,
                type: 1,
                dec_2000: parseInt(formData.dec2000) || 0,
                dec_1000: parseInt(formData.dec1000) || 0,
                dec_500: parseInt(formData.dec500) || 0,
                dec_200: parseInt(formData.dec200) || 0,
                dec_100: parseInt(formData.dec100) || 0,
                dec_50: parseInt(formData.dec50) || 0,
                dec_20: parseInt(formData.dec20) || 0,
                dec_10: parseInt(formData.dec10) || 0,
                dec_5: parseInt(formData.dec5) || 0,
                dec_2: parseInt(formData.dec2) || 0,
                dec_1: parseInt(formData.dec1) || 0,
                ...(multiCurrency ? { currency_balances: buildCurrencyBalances(foreignCurrencies, foreignValues) } : {})
            });

            onClose();
            onSuccess();
        } catch (error) {
            console.error('Error abriendo la caja:', error);
            const message = error?.response?.data?.error_message || "Error al abrir la caja";
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => {}} maxWidth="sm" fullWidth>
            <DialogTitle>{t('open_cash_register')}</DialogTitle>
            <DialogContent>
                {discrepancy && (
                    <Typography color="error" sx={{ mb: 2 }}>
                        {t('discrepancy_warning')}
                    </Typography>
                )}
                {multiCurrency && <CashCurrencyStepper foreignCurrencies={foreignCurrencies} step={step} />}
                {step > 0 && <ForeignCurrencyCashForm currency={foreignCurrencies[step - 1]} values={foreignValues} onChange={setForeignValues} />}
                {step === 0 && multiCurrency && <Typography variant="h6" sx={{ mb: 0.5 }}>{currencyHeading(DEFAULT_CURRENCY)}</Typography>}
                {step === 0 && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {multiCurrency ? 'Contá los billetes y monedas en pesos que hay en caja. Las otras monedas van en el paso siguiente.' : 'Contá los billetes y monedas que hay en caja.'}
                </Typography>}
                {step === 0 && <Grid container spacing={2}>
                    {["dec2000", "dec1000", "dec500", "dec200", "dec100", "dec50", "dec20", "dec10", "dec5", "dec2", "dec1"].map((field) => (
                        <Grid item xs={6} key={field}>
                            <TextField
                                style={{ marginTop: "6px" }}
                                label={field.replace("dec", "Cantidad de $")}
                                name={field}
                                fullWidth
                                onChange={handleChange}
                                type="number"
                                inputProps={{ min: 0 }}
                                error={fieldErrors[field]}
                                helperText={fieldErrors[field] ? t('count_again') : ''}
                            />
                        </Grid>
                    ))}
                </Grid>}
            </DialogContent>
            <DialogActions>
                {step > 0 ? <Button onClick={() => setStep(step - 1)}>Atrás</Button> : <Button onClick={onClose}>{t('cancel')}</Button>}
                <LoadingButton onClick={handleSubmit} loading={loading} variant="contained" color="primary">
                    {multiCurrency && step < foreignCurrencies.length ? 'Siguiente' : t('confirm')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default OpenCashRegisterModal;
